import { useEffect, useRef, useState } from "react";
import { readPlayerRequestViewport } from "../lib/playerRequestViewport";

const round = (value) => Math.round(Number(value) || 0);

export default function PlayerRequestViewportDebug({ enabled, shellRef }) {
  const [snapshot, setSnapshot] = useState(null);
  const eventsRef = useRef([]);

  useEffect(() => {
    if (!enabled) {
      setSnapshot(null);
      eventsRef.current = [];
      return undefined;
    }

    const viewport = window.visualViewport;
    let frame = null;
    let pendingEvents = [];
    const capture = () => {
      frame = null;
      const shell = shellRef.current;
      const header = shell?.querySelector(".player-request-chat-header");
      const composer = shell?.querySelector(".player-request-composer-footer");
      const projection = readPlayerRequestViewport(viewport, window.innerHeight);
      const metrics = {
        event: pendingEvents[pendingEvents.length - 1] || "init",
        innerHeight: round(window.innerHeight),
        viewportHeight: round(viewport?.height || window.innerHeight),
        offsetTop: round(viewport?.offsetTop),
        scrollY: round(window.scrollY),
        keyboardInset: projection.keyboardInset,
        shellTop: round(shell?.getBoundingClientRect().top),
        shellBottom: round(shell?.getBoundingClientRect().bottom),
        headerTop: round(header?.getBoundingClientRect().top),
        composerBottom: round(composer?.getBoundingClientRect().bottom),
      };
      const labels = pendingEvents.length ? pendingEvents : ["init"];
      pendingEvents = [];
      eventsRef.current = [
        ...eventsRef.current,
        ...labels.map((event) => `${event} h=${metrics.viewportHeight} top=${metrics.offsetTop} inset=${metrics.keyboardInset}`),
      ].slice(-10);
      setSnapshot({ ...metrics, events: eventsRef.current });
    };
    const scheduleCapture = (event) => {
      pendingEvents.push(event);
      if (frame === null) frame = window.requestAnimationFrame(capture);
    };
    const onResize = () => scheduleCapture("vv-resize");
    const onScroll = () => scheduleCapture("vv-scroll");
    const onFocus = (event) => {
      if (event.target?.matches?.(".player-request-composer-input")) scheduleCapture("focus");
    };
    const onBlur = (event) => {
      if (event.target?.matches?.(".player-request-composer-input")) scheduleCapture("blur");
    };

    scheduleCapture("init");
    viewport?.addEventListener("resize", onResize);
    viewport?.addEventListener("scroll", onScroll);
    document.addEventListener("focusin", onFocus);
    document.addEventListener("focusout", onBlur);
    return () => {
      viewport?.removeEventListener("resize", onResize);
      viewport?.removeEventListener("scroll", onScroll);
      document.removeEventListener("focusin", onFocus);
      document.removeEventListener("focusout", onBlur);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [enabled, shellRef]);

  if (!enabled || !snapshot) return null;
  return <aside
    className="fixed right-2 z-[100] max-w-[calc(100vw-16px)] rounded-lg px-2 py-1.5 font-mono text-[9px] leading-3 pointer-events-none"
    style={{
      bottom: "calc(var(--pc-keyboard-inset, 0px) + 76px)",
      width: 282,
      color: "#d8fbff",
      background: "rgba(0, 10, 16, .9)",
      border: "1px solid rgba(38, 231, 255, .45)",
    }}
    data-testid="player-request-viewport-debug"
  >
    <div>event {snapshot.event}</div>
    <div>inner {snapshot.innerHeight} · vv {snapshot.viewportHeight} · top {snapshot.offsetTop} · scroll {snapshot.scrollY}</div>
    <div>inset {snapshot.keyboardInset} · shell {snapshot.shellTop}/{snapshot.shellBottom}</div>
    <div>header {snapshot.headerTop} · composer {snapshot.composerBottom}</div>
    <div className="mt-1 opacity-75">{snapshot.events.map((event, index) => <div key={`${index}-${event}`}>{event}</div>)}</div>
  </aside>;
}
